import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle, LoaderCircle, Search } from "lucide-react";
import LotteryCard from "./LotteryCard.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Switch } from "@/components/ui/switch.tsx";
import { useNavigate } from "react-router";
import { getDraws } from "@/api/getDraws.ts";
import { Draw } from "@/types/draw.ts";
import debounce from "lodash.debounce";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert.tsx";
import { useTranslation } from "@/i18n";

const LotteriesList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [draws, setDraws] = useState<Draw[]>();
  const [searchQuery, setSearchQuery] = useState<string>();
  const [sortOption, setSortOption] = useState("popular");
  const [showOnlyAvailable, setShowOnlyAvailable] = useState(true);
  const [error, setError] = useState<string>();

  const fetchDraws = debounce(async () => {
    setLoading(true);
    try {
      const sortOrder = ["price-low-high", "prize-low-high"].includes(
        sortOption,
      )
        ? "asc"
        : "desc";
      const response = await getDraws({
        status: showOnlyAvailable ? ["live", "scheduled"] : undefined,
        search: searchQuery?.trim(),
        sortKey: sortOption.split("-")[0],
        sortOrder,
      });
      setDraws(response.data.results);
    } catch (e: unknown) {
      setError(t("raffles.fetchFailed"));
    } finally {
      setLoading(false);
    }
  }, 500);

  useEffect(() => {
    if (loading) return;
    fetchDraws();
  }, [showOnlyAvailable, searchQuery, sortOption]);

  useEffect(() => {
    fetchDraws();
  }, []);

  const handleSelect = (draw: Draw) => {
    navigate(`/raffles/${draw.id}`);
  };

  return (
    <div className="container mx-auto">
      <div className="mb-8 flex flex-col items-start justify-end gap-4 md:flex-row md:items-center">
        <div className="flex w-full flex-col gap-4 sm:flex-row md:w-auto">
          <div className="bg-card relative flex-grow">
            <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 transform" />
            <Input
              placeholder={t("raffles.searchPlaceholder")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <Select value={sortOption} onValueChange={setSortOption}>
            <SelectTrigger className="bg-card w-full sm:w-[180px]">
              <SelectValue placeholder={t("raffles.sortBy")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="popular">
                {t("raffles.sortPopular")}
              </SelectItem>
              <SelectItem value="new">{t("raffles.sortNewest")}</SelectItem>
              <SelectItem value="price-low-high">
                {t("raffles.sortPriceLowHigh")}
              </SelectItem>
              <SelectItem value="price-high-low">
                {t("raffles.sortPriceHighLow")}
              </SelectItem>
              <SelectItem value="prize-low-high">
                {t("raffles.sortPrizeLowHigh")}
              </SelectItem>
              <SelectItem value="prize-high-low">
                {t("raffles.sortPrizeHighLow")}
              </SelectItem>
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2">
            <Switch
              id="live-toggle"
              checked={showOnlyAvailable}
              onCheckedChange={setShowOnlyAvailable}
            />
            <Label htmlFor="live-toggle" className="cursor-pointer">
              {t("raffles.liveAndScheduledOnly")}
            </Label>
          </div>
        </div>
      </div>

      {loading && <LoaderCircle className="mx-auto size-6 animate-spin" />}
      <div className="flex flex-col gap-8 md:flex-row">
        <div className="flex-1">
          {!!error && (
            <Alert variant="destructive" className="mx-auto my-4 w-fit">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t("common.somethingWentWrong")}</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          {draws?.length ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {draws.map((draw) => (
                <LotteryCard
                  key={draw.id}
                  draw={draw}
                  onSelect={handleSelect}
                />
              ))}
            </div>
          ) : (
            !loading && (
              <div className="py-12 text-center">
                <h3 className="mb-2 text-xl font-medium">
                  {t("raffles.noneFound")}
                </h3>
                <p className="text-muted-foreground">
                  {t("raffles.adjustFilters")}
                </p>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default LotteriesList;
